<template>
  <!-- Filter-aware money total for a wallet transaction list. Degrades to nothing when the
       backend omits `totals` (older build), so it is safe to deploy ahead of the API. -->
  <div v-if="totals" class="wallet-list-totals">
    <div
        v-for="bucket in visibleBuckets"
        :key="bucket.key"
        :class="['box', 'wlt-card', bucket.key]"
    >
      <div class="wlt-head">
        <span class="tag" :class="bucket.tagClass">{{ bucket.label }}</span>
        <span class="wlt-head-label">{{ mode === 'inflow' ? 'ລວມເງິນເຂົ້າ' : 'ຍອດສຸດທິ' }}</span>
      </div>

      <!-- Inflow-only (pending top-ups): out is always 0 and net == in, so show just the inflow. -->
      <div v-if="mode === 'inflow'" class="wlt-net">
        {{ formatCurrency(bucket.data.in) }}
      </div>

      <template v-else>
        <div class="wlt-net" :class="{ negative: isNegative(bucket.data.net) }">
          {{ formatCurrency(bucket.data.net) }}
        </div>
        <!-- The five figures under the net, laid out as an aligned label-left / value-right grid so
             the numbers line up in one orderly column (was a ragged flex-wrap row). -->
        <dl class="wlt-figures">
          <div class="wlt-fig">
            <dt>ເຂົ້າ</dt>
            <dd>{{ formatCurrency(bucket.data.in) }}</dd>
          </div>
          <div class="wlt-fig">
            <dt>ອອກ</dt>
            <dd>{{ formatCurrency(bucket.data.out) }}</dd>
          </div>
          <div class="wlt-fig">
            <dt>ລາຍການ</dt>
            <dd>{{ formatNumber(bucket.data.count) }}</dd>
          </div>
          <template v-if="bucket.data.opening_balance != null">
            <div class="wlt-fig wlt-fig--divide">
              <dt>ຍອດຍົກມາ</dt>
              <dd :class="{ negative: isNegative(bucket.data.opening_balance) }">{{ formatCurrency(bucket.data.opening_balance) }}</dd>
            </div>
            <div class="wlt-fig">
              <dt>ຍອດຍົກໄປ</dt>
              <dd :class="{ negative: isNegative(bucket.data.closing_balance) }">{{ formatCurrency(bucket.data.closing_balance) }}</dd>
            </div>
          </template>
        </dl>
      </template>
    </div>
  </div>
</template>

<script>
import { formatCurrency, formatNumber } from '@/services/walletFormatters';

// Zero-fill so a missing wallet bucket still renders 0 instead of "-" / NaN. Both backends
// (Go ledger + Laravel SWT) always send both keys, but this keeps the component defensive.
const ZERO_BUCKET = { in: '0', out: '0', net: '0', count: 0, opening_balance: null, closing_balance: null };

const BUCKET_META = {
  freight: { label: 'ຄ່າຂົນສົ່ງ', tagClass: 'is-info' },
  cod: { label: 'COD', tagClass: 'is-warning' },
};

export default {
  name: 'WalletListTotals',
  props: {
    // The `totals` object from the list response: { freight:{in,out,net,count}, cod:{...} }.
    // Decimal money fields are strings; formatCurrency handles them directly.
    totals: {
      type: Object,
      default: null,
    },
    // 'full' renders net + in/out/count; 'inflow' renders the inflow sum only (pending top-ups).
    mode: {
      type: String,
      default: 'full',
      validator: (v) => ['full', 'inflow'].includes(v),
    },
    // Restrict to a single wallet bucket (the per-branch dialog filters one wallet_type at a time).
    only: {
      type: String,
      default: '',
      validator: (v) => ['', 'freight', 'cod'].includes(v),
    },
  },
  computed: {
    visibleBuckets() {
      const keys = this.only ? [this.only] : ['freight', 'cod'];
      return keys
        .filter((k) => BUCKET_META[k])
        .map((k) => ({
          key: k,
          label: BUCKET_META[k].label,
          tagClass: BUCKET_META[k].tagClass,
          data: { ...ZERO_BUCKET, ...((this.totals && this.totals[k]) || {}) },
        }));
    },
  },
  methods: {
    formatCurrency,
    formatNumber,
    // Sign off the decimal string directly — Number("-50000.00") < 0 is exact for these magnitudes.
    isNegative(net) {
      return Number(net) < 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.wallet-list-totals {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.wlt-card {
  flex: 1 1 240px;
  border-left: 4px solid #3273dc;
  margin-bottom: 0;

  &.cod {
    border-left-color: #f0ad4e;
  }
}

.wlt-head {
  color: #666;
  font-size: 14px;
  margin-bottom: 6px;

  .wlt-head-label {
    margin-left: 6px;
  }
}

.wlt-net {
  font-size: 24px;
  font-weight: 700;

  &.negative {
    color: #dc3545;
  }
}

.wlt-figures {
  margin: 8px 0 0;
  font-size: 15px;
}

.wlt-fig {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 2px 0;

  dt {
    color: #888;
  }

  dd {
    margin: 0;
    color: #555;
    font-weight: 500;
    // Align digits so the values form a clean right-hand column.
    font-variant-numeric: tabular-nums;
  }

  .negative {
    color: #dc3545;
  }
}

// Dashed rule before the brought-/carried-forward pair, keeping the original visual grouping.
.wlt-fig--divide {
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px dashed #ddd;
}
</style>
